import { useRef, useState, useCallback } from "react";
import { apiGet, getApiBaseUrl } from "../api/http";

const WS_BASE = getApiBaseUrl().replace(/^http/, "ws");

/**
 * Hook that manages a WebSocket connection to track audit progress.
 * Returns { progress, startAudit, reset }
 *
 * progress = { step, totalSteps, label, percent, done, error, result }
 */
export function useAuditProgress() {
  const [progress, setProgress] = useState(null);
  const wsRef = useRef(null);
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPollingFallback = useCallback((auditId, progressPath = "/api/audit/progress") => {
    if (!auditId) return;
    stopPolling();

    const pollForResult = async () => {
      try {
        const { data } = await apiGet(`${progressPath}/${auditId}`);
        if (data) {
          const recovered =
            data.done && !String(data.label || "").includes("Recovered via polling")
              ? { ...data, label: `${data.label || "Complete!"} (Recovered via polling)` }
              : data;
          setProgress(recovered);
          if (recovered.done || recovered.error) {
            stopPolling();
          }
        }
      } catch (err) {
        if (err?.response?.status === 404) return;
        // Keep polling quietly; transient failures are expected on flaky networks.
      }
    };

    setProgress((prev) => prev || { label: "Connection lost. Retrying via polling...", percent: 0 });
    pollForResult();
    pollTimerRef.current = setInterval(pollForResult, 3000);
  }, [stopPolling]);

  const reset = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    stopPolling();
    setProgress(null);
  }, [stopPolling]);

  /**
   * Open a WebSocket, subscribe to the given auditId, and track progress.
   */
  const subscribe = useCallback((auditId, options = {}) => {
    const { progressPath = "/api/audit/progress" } = options;
    return new Promise((resolve) => {
      let isTerminal = false;
      const ws = new WebSocket(`${WS_BASE}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "subscribe", auditId }));
        resolve();
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "progress" && msg.auditId === auditId) {
            setProgress(msg);
            if (msg.done || msg.error) {
              isTerminal = true;
              stopPolling();
              ws.close();
              wsRef.current = null;
            }
          }
        } catch { /* ignore */ }
      };

      ws.onerror = () => {
        startPollingFallback(auditId, progressPath);
        wsRef.current = null;
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!isTerminal) {
          startPollingFallback(auditId, progressPath);
        }
      };
    });
  }, [startPollingFallback, stopPolling]);

  return { progress, subscribe, reset };
}
